import { useCallback, useRef, useState } from "react";
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  IconButton,
  FormHelperText,
  InputAdornment,
  CircularProgress,
  List,
  ListItem,
  debounce,
  Popper,
  Link,
  Paper,
  Fade,
  Grow,
} from "@mui/material";
import { keyframes } from "@mui/system";

import { useNavigate } from "react-router-dom";
import AuthenticationAPI from "./api/authenticationApi";
import { Company, Database, UserLogin } from "./models/authTypes";
import { useSnackbar } from "notistack";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import ApplicationUserAPI from "./api/applicationUserApi";
import { useDispatch } from "react-redux";
import { setQueueApiEndpoint, setUser } from "../../app/appSlice";
import { useWorkOrderStore } from "../store/zustandWorkOrderStore";
import {
  clearCustomerLocationLookupCaches,
  clearSchedulingLookupCaches,
} from "../common/Utils/cacheUtil";

import SearchIcon from "@mui/icons-material/Search";
import packageJson from "../../../package.json";
import Swal from "sweetalert2";
import {
  DEFAULT_SEARCH_INFO_ALERT,
  SELECTED_WO_LOCS,
  SELECTED_WO_TECHS,
  SELECTED_WO_statuses,
  FROMDATE,
  TODATE,
  SCHEDULE_FROM_DATE,
  SCHEDULE_TO_DATE,
  DELIVERY_FROM_DATE,
  DELIVERY_TO_DATE,
  PICKUP_FROM_DATE,
  PICKUP_TO_DATE,
  FILTER_CLOCKED_HOURS,
  SELECTED_WO_CUSTOMERS,
  USER_IDENTITY,
  GENERIC_SEARCH,
} from "../common/constants/filterParamConstants";
import {
  logAmplitudeEventWithProps,
  createAmplitudeEventProperties,
} from "components/common/amplitude/amplitude";

type Errors = {
  selectedCompany?: string;
  selectedDatabase?: string;
  username?: string;
  password?: string;
};

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-40px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const slideInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(40px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const logoFloat = keyframes`
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-8px);
  }
`;

const pulseGlow = keyframes`
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(25, 118, 210, 0.4);
  }
  50% {
    box-shadow: 0 0 20px 4px rgba(25, 118, 210, 0.2);
  }
`;

const Login = ({ setAuthenticated }) => {
  const anchorRef = useRef(null); // Ref for anchoring Popper
  const dispatch = useDispatch();
  const { setFilters, resetFilters } = useWorkOrderStore();
  const [userName] = useState<string | "">(
    localStorage.getItem("username") || "",
  );
  const { enqueueSnackbar } = useSnackbar();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [databases, setDatabases] = useState<Database[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<string>("");
  const [selectedDatabase, setSelectedDatabase] = useState<string>("");
  const [username, setUsername] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [errors, setErrors] = useState<Errors>({});
  const navigate = useNavigate();
  const [genSearchVal, setGenSearchVal] = useState<string | null>("");
  const [selectedCompanyName, setSelectedCompanyName] = useState<string>("");
  const [loadingCompanies, setLoadingCompanies] = useState(false);

  const handleCompanyChange = async (company: Company) => {
    const companyId = company.companyIdentity;
    setSelectedCompany(companyId);
    setSelectedDatabase("");
    setGenSearchVal(company.companyName);
    setSelectedCompanyName(company.companyName);
    setCompanies([]);
    // // Fetch databases based on company
    const dbsList = await AuthenticationAPI.getDbsByCompany(companyId);
    if (dbsList) setDatabases(dbsList);
  };
  const styles = document.createElement("style");
  styles.innerHTML = `
    .swal2-container {
      z-index: 2000 !important; /* or a value higher than the MUI dialog's z-index */
    }
  `;
  const validateForm = () => {
    const newErrors: Errors = {};

    if (!selectedCompany) newErrors.selectedCompany = "Company is required.";
    if (!selectedDatabase) newErrors.selectedDatabase = "Database is required.";
    if (!username.trim()) newErrors.username = "Username is required.";
    if (!password.trim()) newErrors.password = "Password is required.";

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const removeItemFromLocalStorage = (key: string) => {
    if (localStorage.getItem(key) !== null) {
      localStorage.removeItem(key);
      //console.log(`Removed ${key} from local storage.`);
    } else {
      //console.log(`${key} does not exist in local storage.`);
    }
  };

  /** Use the username from the successful login (or localStorage after setItem), not `userName` state — that is only set once on mount and is often "" on the login screen. */
  const createCacheItemIfDoesntExist = (resolvedUsername: string) => {
    const user = resolvedUsername.trim();
    if (!user) return;
    const key = `${DEFAULT_SEARCH_INFO_ALERT}_${user}`;
    if (localStorage.getItem(key) === null) {
      localStorage.setItem(key, "true");
    }
  };

  const [isResetting, setIsResetting] = useState(false);
  const handleResetCache = async () => {
    setIsResetting(true);

    try {
      // Clear localStorage & sessionStorage
      localStorage.clear();
      sessionStorage.clear();

      // Clear IndexedDB (optional, async)
      const databases = await indexedDB.databases();
      databases.forEach((db) => {
        if (db.name) indexedDB.deleteDatabase(db.name);
      });

      // Simulating a short delay
      await new Promise((resolve) => setTimeout(resolve, 2000));

      //alert("Cache reset successfully! Please refresh the page.");
      Swal.fire({
        title: "Success",
        html: `Cache reset successfully! Please refresh the page.`,
        icon: "success",
        confirmButtonColor: "#2371E7",
        confirmButtonText: "Ok",
        allowOutsideClick: false,
      });
    } catch (error) {
      // console.error("Error resetting cache:", error);
      // alert("Failed to reset cache. Please try again.");
      Swal.fire({
        title: "Error",
        html: `Resetting Cache Failed`,
        icon: "error",
        confirmButtonColor: "#2371E7",
        confirmButtonText: "OK",
        allowOutsideClick: false,
      });
    } finally {
      setIsResetting(false);
    }
  };
  const progressBarWrapperStyle = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "80vh",
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    zIndex: 1300,
    backgroundColor: "rgba(255, 255, 255, 0.7)", // Add some transparency to indicate loading
  };

  const resetFiltersCache = () => {
    //console.log("Resetting filters cache for user change");

    removeItemFromLocalStorage(`${SELECTED_WO_LOCS}_${userName}`);
    removeItemFromLocalStorage(`${SELECTED_WO_TECHS}_${userName}`);
    removeItemFromLocalStorage(`${SELECTED_WO_statuses}_${userName}`);
    removeItemFromLocalStorage(`${FROMDATE}_${userName}`);
    removeItemFromLocalStorage(`${TODATE}_${userName}`);

    removeItemFromLocalStorage(`${SCHEDULE_FROM_DATE}_${userName}`);
    removeItemFromLocalStorage(`${SCHEDULE_TO_DATE}_${userName}`);

    localStorage.setItem(`${DELIVERY_FROM_DATE}_${userName}`, null);
    localStorage.setItem(`${DELIVERY_TO_DATE}_${userName}`, null);

    localStorage.setItem(`${PICKUP_FROM_DATE}_${userName}`, null);
    localStorage.setItem(`${PICKUP_TO_DATE}_${userName}`, null);

    // removeItemFromLocalStorage(`${VIEW}_${userName}`);
    removeItemFromLocalStorage(`${GENERIC_SEARCH}_${userName}`);
    removeItemFromLocalStorage(`${USER_IDENTITY}`);
    localStorage.setItem(`${FILTER_CLOCKED_HOURS}_${userName}`, "false");
    removeItemFromLocalStorage(`${SELECTED_WO_CUSTOMERS}_${userName}`);
    localStorage.setItem(`${DEFAULT_SEARCH_INFO_ALERT}_${userName}`, "false");

    // Clear lookup data cache (scoped to the user still in localStorage before login completes)
    const cacheUser =
      localStorage.getItem("username") || "currentUserIdentifier";
    removeItemFromLocalStorage(`allCustomerListHeaders_${cacheUser}`);
    removeItemFromLocalStorage(`paymentMethods_${cacheUser}`);
    removeItemFromLocalStorage(`altStockLocations_${cacheUser}`);
    removeItemFromLocalStorage(`allPartListHeaders_${cacheUser}`);
    removeItemFromLocalStorage(`miscCharges_${cacheUser}`);
    removeItemFromLocalStorage(`zones_${cacheUser}`);
    removeItemFromLocalStorage(`allUnitListHeaders_${cacheUser}`);
    removeItemFromLocalStorage(`woDocStatuses_${cacheUser}`);
    clearCustomerLocationLookupCaches(cacheUser);

    // Also reset Zustand store filters for the new user
    // This will reset all filters to default values while preserving userIdentity
    //console.log("Calling resetFilters() to clear Zustand store");
    resetFilters();
  };
  const [showProgressBar, setShowProgressBar] = useState(false);
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    setShowProgressBar(true);
    const loginStartTime = Date.now();

    try {
      const payload: UserLogin = {
        CompanyIdentity: selectedCompany,
        DatabaseIdentity: selectedDatabase,
        username,
        password,
        requestFor: "aspen-queue-app",
      };
      const authResponse = await AuthenticationAPI.authenticateUser(payload);
      if (!authResponse || typeof authResponse !== "object") {
        enqueueSnackbar("Unexpected server response. Please try again.", {
          variant: "error",
        });
        return;
      }
      if (authResponse.success && authResponse.data) {
        if (
          !authResponse.data.token ||
          !authResponse.data.aspenQueueAppEndpoint
        ) {
          enqueueSnackbar(
            "Authentication succeeded but server response is incomplete.",
            { variant: "error" },
          );
          return;
        }
        localStorage.setItem("authToken", authResponse.data.token);
        localStorage.setItem(
          "queueAPI",
          authResponse.data.aspenQueueAppEndpoint,
        );
        localStorage.setItem("username", username);
        clearSchedulingLookupCaches(username);
        dispatch(setQueueApiEndpoint(authResponse.data.aspenQueueAppEndpoint));
        const userInfo = await ApplicationUserAPI.getUserByUserName(username);
        if (!userInfo) {
          localStorage.removeItem("authToken");
          localStorage.removeItem("queueAPI");
          localStorage.removeItem("username");
          enqueueSnackbar(
            "Failed to fetch user info. Please contact support.",
            { variant: "error" },
          );
          return;
        }

        const companyName = selectedCompanyName || genSearchVal || "";
        userInfo.userNameAndCompany = `${userInfo.userName}@${companyName}`;
        userInfo.company = companyName;

        if (username !== userName) {
          resetFiltersCache();
        }
        await dispatch(setUser(userInfo));
        localStorage.setItem(
          `${USER_IDENTITY}`,
          userInfo.identity?.toString() || "",
        );
        setFilters("userIdentity", userInfo.identity);
        localStorage.setItem("user", JSON.stringify(userInfo));
        setAuthenticated(true);
        createCacheItemIfDoesntExist(username);

        const elapsedMs = Date.now() - loginStartTime;
        const userAndCompany =
          userInfo.userNameAndCompany || `${userInfo.userName}@${companyName}`;
        logAmplitudeEventWithProps(
          userAndCompany,
          { company: companyName, user: userInfo.userName || "" },
          "Login",
          createAmplitudeEventProperties(
            "Login",
            "Login.tsx",
            packageJson.version,
            elapsedMs.toString(),
          ),
        );

        window.dispatchEvent(new CustomEvent("userChanged"));

        navigate("/QueueBoard", { replace: true });
      } else if (authResponse && authResponse.error === "402") {
        enqueueSnackbar(
          "The selected company is not licensed to use Queue App",
          { variant: "error" },
        );
        return;
      } else {
        enqueueSnackbar(
          "Login failed. Please check your credentials and try again.",
          { variant: "error" },
        );
        return;
      }
    } catch (exception: any) {
      localStorage.removeItem("authToken");
      localStorage.removeItem("queueAPI");
      localStorage.removeItem("username");
      localStorage.removeItem("user");
      enqueueSnackbar("Something went wrong during Login. Please try again.", {
        variant: "error",
      });
    } finally {
      setShowProgressBar(false);
    }
  };
  const handleShowPasswordChange = () => setShowPassword(!showPassword);

  const handleCompanySearch = (event) => {
    const query = event.target.value;
    setGenSearchVal(event.target.value);
    debouncedFetchCompanies(query);
    //logAmplitudeEvent("Company Search", "handleCompanySearch-Login");
  };

  // Function to fetch customers from the API
  const fetchCompanies = async (query) => {
    setLoadingCompanies(true);
    try {
      const companyList = await AuthenticationAPI.getCompany(query);
      if (companyList) setCompanies(companyList);
    } catch (error) {
      console.error("Error fetching customers:", error);
      navigate("/error");
    } finally {
      setLoadingCompanies(false);
    }
    //logAmplitudeEvent("Fetching companies", "fetchCompanies-Login");
  };

  const [open, setOpen] = useState(false);
  // Debounced function to delay the API call
  const debouncedFetchCompanies = useCallback(
    debounce((query) => {
      if (query.length >= 3) {
        fetchCompanies(query);
      } else {
        setCompanies([]);
        setDatabases([]);
        setSelectedCompany("");
      }
    }, 500),
    [],
  );
  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#f9f9f9",
        px: { xs: 1.5, sm: 3, md: 4 },
        py: { xs: 3, md: 5 },
        overflow: "hidden",
      }}
    >
      <Grow in={true} timeout={600}>
        <Box
          sx={{
            width: "100%",
            maxWidth: { xs: "100%", sm: "540px", md: "840px", lg: "960px" },
            boxShadow: { xs: 1, sm: "0 8px 40px rgba(0,0,0,0.12)" },
            backgroundColor: "#fff",
            borderRadius: 2,
            overflow: "hidden",
            animation: `${fadeInUp} 0.7s ease-out`,
          }}
        >
          <Grid container direction={{ xs: "column", md: "row" }}>
            {/* Left Column */}
            <Grid
              item
              xs={12}
              md={5}
              sx={{
                backgroundColor: "#181F2A",
                padding: { xs: 3, sm: 4 },
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                alignItems: "center",
                textAlign: "center",
                borderRight: { xs: "none", md: "1px solid #e0e0e0" },
                borderBottom: { xs: "1px solid #e0e0e0", md: "none" },
                animation: `${slideInLeft} 0.8s ease-out 0.2s both`,
              }}
            >
              {/* Header Section */}
              <Box
                sx={{
                  animation: `${fadeInUp} 0.6s ease-out 0.4s both`,
                }}
              >
                <Typography
                  color="white"
                  variant="h4"
                  fontWeight="bold"
                  gutterBottom
                  sx={{
                    animation: `${fadeInUp} 0.6s ease-out 0.5s both`,
                  }}
                >
                  Login
                </Typography>
                <Typography
                  color="white"
                  variant="body1"
                  sx={{
                    animation: `${fadeInUp} 0.6s ease-out 0.6s both`,
                  }}
                >
                  Welcome back! <br /> Please enter your details.
                </Typography>
              </Box>

              {/* Footer Section */}
              <Box
                sx={{
                  animation: `${fadeInUp} 0.6s ease-out 0.7s both`,
                }}
              >
                <Typography variant="body2" sx={{ color: "", marginBottom: 2 }}>
                  <Link
                    href="https://customer.chartersoftware.com/knowledgeBase/category/46401/subcategory/58944"
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                    color="primary"
                  >
                    Version {packageJson.version}
                  </Link>
                </Typography>
                <Box
                  component="img"
                  src="/aspen-logo-service-queue-white-2x.svg"
                  alt="Aspen Logo"
                  sx={{
                    width: 150,
                    animation: `${logoFloat} 3s ease-in-out infinite`,
                    animationDelay: "1s",
                  }}
                />
              </Box>
            </Grid>

            {/* Right Column */}
            <Grid
              item
              xs={12}
              md={7}
              sx={{
                padding: { xs: 3, sm: 4 },
                animation: `${slideInRight} 0.8s ease-out 0.3s both`,
              }}
            >
              <Box
                component="form"
                noValidate
                autoComplete="off"
                onSubmit={handleSubmit}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: { xs: 1.5, sm: 1.5 },
                }}
              >
                {/* Company Name */}
                <Box sx={{ animation: `${fadeInUp} 0.5s ease-out 0.5s both` }}>
                  <Box ref={anchorRef} sx={{ width: "100%" }}>
                    <TextField
                      fullWidth
                      label="Company"
                      variant="outlined"
                      size="small"
                      error={!!errors.selectedCompany}
                      onChange={(event) => {
                        handleCompanySearch(event);
                        setOpen(true);
                      }}
                      onBlur={() => setTimeout(() => setOpen(false), 200)}
                      onFocus={() => setOpen(companies.length > 0)}
                      name="GENERIC_SEARCH"
                      value={genSearchVal}
                      placeholder="Search Company"
                      InputProps={{
                        endAdornment: loadingCompanies ? (
                          <CircularProgress size={20} />
                        ) : (
                          <SearchIcon />
                        ),
                      }}
                      helperText={errors.selectedCompany}
                      sx={{
                        minWidth: "10%",
                        "& .MuiInputBase-input::placeholder": {
                          textAlign: "left",
                        },
                      }}
                    />

                    <Popper
                      open={open && companies.length > 0}
                      anchorEl={anchorRef.current}
                      placement="bottom-start"
                      modifiers={[
                        {
                          name: "preventOverflow",
                          options: {
                            boundary: "window", // Prevents overlapping with other elements
                          },
                        },
                        {
                          name: "offset",
                          options: {
                            offset: [0, 2], // Adds space between the field and list
                          },
                        },
                      ]}
                      sx={{
                        zIndex: 1300, // Ensures dropdown appears above other elements
                        width: anchorRef.current
                          ? `${anchorRef.current.offsetWidth}px`
                          : "auto", // Dynamic width
                      }}
                    >
                      <Paper
                        sx={{
                          width: "100%", // <-- Ensures full width
                          maxHeight: 200,
                          overflowY: "auto",
                          backgroundColor: "white", // Ensure background remains white
                          borderRadius: "8px", // Rounded corners
                          boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.15)", // Add shadow for depth
                          border: "1px solid #ddd", // Light border for separation
                          //mt: 1, // Small margin to prevent sticking to TextField
                        }}
                      >
                        <List>
                          {companies.map((company) => (
                            <ListItem
                              key={company.companyIdentity}
                              onClick={() => {
                                handleCompanyChange(company);
                                setOpen(false);
                              }}
                              sx={{
                                cursor: "pointer",
                                padding: "8px",

                                "&:hover": { backgroundColor: "#f0f0f0" },
                              }}
                            >
                              {company.companyName}
                            </ListItem>
                          ))}
                        </List>
                      </Paper>
                    </Popper>
                  </Box>
                </Box>
                {/* Database */}
                <FormControl
                  error={!!errors.selectedDatabase}
                  size="small"
                  variant="outlined"
                  fullWidth
                  sx={{ animation: `${fadeInUp} 0.5s ease-out 0.6s both` }}
                >
                  <InputLabel id="database-label">Database</InputLabel>
                  <Select
                    disabled={!selectedCompany}
                    labelId="database-label"
                    id="database"
                    fullWidth
                    size="small"
                    label="Company"
                    placeholder="Enter your company name"
                    variant="outlined"
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 200, // Set dropdown height
                          overflowY: "auto", // Add scrollbar if needed
                        },
                      },
                    }}
                    value={selectedDatabase}
                    onChange={(e) => setSelectedDatabase(e.target.value)}
                  >
                    {databases.map((db) => (
                      <MenuItem
                        key={db.databaseIdentity}
                        value={db.databaseIdentity}
                      >
                        {db.databaseName}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{errors.selectedDatabase}</FormHelperText>
                </FormControl>
                {/* Username */}
                <TextField
                  id="username"
                  label="Username"
                  variant="outlined"
                  size="small"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  error={!!errors.username}
                  helperText={errors.username}
                  sx={{ animation: `${fadeInUp} 0.5s ease-out 0.7s both` }}
                />
                {/* Password */}
                <TextField
                  id="password"
                  size="small"
                  label="Password"
                  variant="outlined"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  error={!!errors.password}
                  helperText={errors.password}
                  sx={{ animation: `${fadeInUp} 0.5s ease-out 0.8s both` }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          id="passwordVisibilityBtn"
                          onClick={handleShowPasswordChange}
                        >
                          {showPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                {/* Forgot Password */}
                {/* <Typography variant="body2" align="right">
                <a
                  href="#"
                  style={{ textDecoration: "none", color: "#007bff" }}
                >
                  Forgot your password? Reset it
                </a>
              </Typography> */}
                {/* Login Button */}
                <Button
                  type="submit"
                  variant="contained"
                  size="small"
                  fullWidth
                  sx={{
                    mt: 1,
                    textTransform: "capitalize",
                    backgroundColor: "#1976d2",
                    animation: `${fadeInUp} 0.5s ease-out 0.9s both`,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: "0 6px 20px rgba(25, 118, 210, 0.4)",
                    },
                    "&:active": {
                      transform: "translateY(0)",
                    },
                  }}
                >
                  Login
                </Button>

                <Box
                  mt={1}
                  sx={{
                    textAlign: { xs: "center", sm: "right" },
                    animation: `${fadeInUp} 0.5s ease-out 1s both`,
                  }}
                >
                  {isResetting ? (
                    <CircularProgress size={20} />
                  ) : (
                    <Typography
                      variant="body2"
                      color="primary"
                      sx={{ cursor: "pointer", textDecoration: "underline" }}
                      onClick={handleResetCache}
                    >
                      Having issues? [Reset Cache]
                    </Typography>
                  )}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Grow>

      {showProgressBar && (
        <Box sx={progressBarWrapperStyle}>
          <CircularProgress sx={{ color: "#2371E7" }} size={48} />
        </Box>
      )}
    </Box>
  );
};

export default Login;
